import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Footprints,
  Droplet,
  Moon,
  Heart,
  Activity,
  Flame,
  Scale,
  Thermometer,
  Wind,
  CheckCircle2,
  Circle,
  Sparkles,
  Bell,
  Calendar,
  Phone,
  ChevronRight,
  Plus,
  Trophy,
  MapPin,
  Utensils,
  ArrowLeft,
  Sun as SunIcon,
  Watch,
  BedDouble,
  Users,
  MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import {
  loadHealth,
  updateToday,
  updateProfile,
  toggleMission,
  computeBMI,
  bmiCategory,
  goodMorning,
} from "@/lib/healthStore";
import { useChatbot } from "@/context/ChatbotContext";
import { useMental } from "@/context/MentalContext";
import TrendsPanel from "@/components/silentscan/TrendsPanel";
import ScoreHistory from "@/components/silentscan/ScoreHistory";

const MISSIONS = [
  { id: "water", label: "Drink 8 glasses of water", xp: 20 },
  { id: "walk", label: "Take a 15-min walk", xp: 25 },
  { id: "breath", label: "1-min box breathing", xp: 15 },
  { id: "sleep", label: "Log tonight's sleep", xp: 10 },
  { id: "mood", label: "Note today's mood", xp: 10 },
];

const MOODS = [
  { key: "great", emoji: "😀", label: "Great" },
  { key: "good", emoji: "🙂", label: "Good" },
  { key: "okay", emoji: "😐", label: "Okay" },
  { key: "low", emoji: "😔", label: "Low" },
  { key: "bad", emoji: "😢", label: "Rough" },
];

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.05, delayChildren: 0.05 } },
};
const item = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

export default function Dashboard() {
  const [state, setState] = useState(() => loadHealth());
  const [nameDraft, setNameDraft] = useState(state.profile.name || "");
  const { openChatbot } = useChatbot();
  const { openMental } = useMental();

  useEffect(() => {
    // scroll to top on load
    window.scrollTo(0, 0);
  }, []);

  const bmi = useMemo(
    () => computeBMI(state.profile.height, state.profile.weight || state.today.weight),
    [state.profile, state.today.weight]
  );
  const bmiCat = bmiCategory(bmi);
  const todayMissions = state.missions_done[state.today.date] || [];
  const missionProgress = Math.round((todayMissions.length / MISSIONS.length) * 100);

  const patch = (key, value) => {
    const s = updateToday({ [key]: value });
    setState({ ...s });
  };

  const bumpWater = (amount) => {
    const cur = state.today.water_ml || 0;
    const s = updateToday({ water_ml: Math.max(0, cur + amount) });
    setState({ ...s });
  };

  const bumpSteps = (delta) => {
    const cur = state.today.steps || 0;
    const s = updateToday({ steps: Math.max(0, cur + delta) });
    setState({ ...s });
  };

  const onMission = (id) => {
    const s = toggleMission(id);
    setState({ ...s });
    const done = (s.missions_done[s.today.date] || []).includes(id);
    if (done) toast.success("Mission completed +XP");
  };

  const setBP = (systolic, diastolic) => {
    patch("blood_pressure", {
      systolic: Number(systolic) || null,
      diastolic: Number(diastolic) || null,
    });
  };

  const saveName = () => {
    const s = updateProfile({ name: nameDraft.trim() });
    setState({ ...s });
    toast.success("Profile updated");
  };

  return (
    <div className="min-h-screen bg-[#05050A] text-white pb-24">
      {/* Ambient background */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 opacity-70"
        style={{
          background:
            "radial-gradient(60% 40% at 20% 0%, rgba(11,61,145,0.35) 0%, transparent 60%), radial-gradient(50% 40% at 90% 20%, rgba(0,229,255,0.15) 0%, transparent 60%)",
        }}
      />

      {/* Top nav */}
      <div className="border-b border-white/5 sticky top-0 z-30 backdrop-blur-md bg-black/40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            data-testid="dashboard-back-home"
            className="inline-flex items-center gap-2 text-zinc-300 hover:text-white text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            SilentScan<span className="text-cyan-400">.AI</span>
          </Link>
          <div className="hidden sm:flex items-center gap-3 text-[11px] uppercase tracking-widest text-zinc-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Live · Preventive Health
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/founder"
              data-testid="dashboard-founder-link"
              className="text-xs text-zinc-400 hover:text-white px-3 py-1.5 rounded-full border border-white/10 hover:bg-white/5"
            >
              Founder
            </Link>
          </div>
        </div>
      </div>

      <motion.section
        variants={container}
        initial="hidden"
        animate="show"
        className="max-w-7xl mx-auto px-6 pt-10"
      >
        {/* Hero — Good Morning banner */}
        <motion.div variants={item} className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#0B3D91]/40 via-[#0B3D91]/10 to-transparent p-6 sm:p-8 md:p-10">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.35em] text-cyan-300/80">
                Chapter 01 · Today
              </div>
              <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.02]">
                {goodMorning()}
                {state.profile.name ? (
                  <>
                    ,<br className="hidden sm:block" />{" "}
                    <span className="text-cyan-300">{state.profile.name}</span>.
                  </>
                ) : (
                  <>.</>
                )}
              </h1>
              <p className="mt-4 text-zinc-300 max-w-lg leading-relaxed">
                Small daily signals shape your long-term health. Log your day,
                complete three missions, and watch your Twin evolve.
              </p>
              {!state.profile.name && (
                <div className="mt-5 flex items-center gap-2 max-w-sm">
                  <input
                    data-testid="dashboard-name-input"
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    placeholder="What should we call you?"
                    className="flex-1 rounded-full bg-white/5 border border-white/10 px-4 py-2 text-sm placeholder-zinc-500 focus:outline-none focus:border-cyan-400"
                  />
                  <button
                    data-testid="dashboard-name-save"
                    onClick={saveName}
                    className="px-4 py-2 rounded-full bg-cyan-500 text-black font-medium text-sm hover:bg-cyan-400"
                  >
                    Save
                  </button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-4 sm:gap-3 self-end sm:self-auto">
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                  Streak
                </div>
                <div className="mt-1 font-display text-3xl sm:text-4xl font-semibold text-amber-300 tabular-nums">
                  {state.streak}
                </div>
                <div className="text-[10px] text-zinc-500">days</div>
              </div>
              <div className="w-px h-14 bg-white/10" />
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold whitespace-nowrap">
                  Today · Missions
                </div>
                <div className="mt-1 font-display text-3xl sm:text-4xl font-semibold text-cyan-300 tabular-nums">
                  {todayMissions.length}/{MISSIONS.length}
                </div>
                <div className="text-[10px] text-zinc-500">{missionProgress}% complete</div>
              </div>
            </div>
          </div>

          {/* subtle progress bar */}
          <div className="mt-6 h-1 w-full rounded-full bg-white/5 overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-cyan-400 to-emerald-400"
              initial={{ width: 0 }}
              animate={{ width: `${missionProgress}%` }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>

          {/* Actions */}
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              data-testid="dashboard-start-checkin"
              onClick={openChatbot}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-black font-medium text-sm hover:bg-cyan-100 transition-colors"
            >
              <Sparkles className="w-4 h-4" />
              Run today&apos;s AI check-in
            </button>
            <button
              data-testid="dashboard-mental"
              onClick={openMental}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-white text-sm hover:bg-white/5"
            >
              <Heart className="w-4 h-4 text-rose-400" />
              Mental wellness check-in
            </button>
            <a
              data-testid="dashboard-hospital"
              href="https://www.google.com/maps/search/hospital+near+me"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-white text-sm hover:bg-white/5"
            >
              <MapPin className="w-4 h-4 text-cyan-400" />
              Find nearest hospital
            </a>
          </div>
        </motion.div>

        {/* Metric grid */}
        <motion.div variants={item} className="mt-8">
          <SectionHeader chapter="Chapter 02" title="Today's vitals" subtitle="Tap to log — everything lives on your device." />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <MetricCard
              Icon={Footprints}
              label="Steps"
              value={state.today.steps}
              unit=""
              accent="#22d3ee"
              testId="metric-steps"
            >
              <div className="mt-3 flex gap-2">
                <StepChip onClick={() => bumpSteps(500)}>+500</StepChip>
                <StepChip onClick={() => bumpSteps(1000)}>+1k</StepChip>
                <StepChip onClick={() => bumpSteps(-500)}>-500</StepChip>
              </div>
            </MetricCard>

            <MetricCard
              Icon={Droplet}
              label="Water"
              value={`${((state.today.water_ml || 0) / 250).toFixed(0)} glasses`}
              unit=""
              accent="#60a5fa"
              testId="metric-water"
            >
              <div className="mt-3 flex gap-2">
                <StepChip onClick={() => bumpWater(250)}>+glass</StepChip>
                <StepChip onClick={() => bumpWater(-250)}>-glass</StepChip>
              </div>
            </MetricCard>

            <MetricCard
              Icon={Moon}
              label="Sleep"
              value={state.today.sleep_hours || 0}
              unit="hrs"
              accent="#a78bfa"
              testId="metric-sleep"
            >
              <input
                data-testid="metric-sleep-input"
                type="number"
                min="0"
                max="16"
                step="0.5"
                placeholder="Hours"
                value={state.today.sleep_hours || ""}
                onChange={(e) => patch("sleep_hours", Number(e.target.value))}
                className="mt-2 w-full rounded-lg bg-white/5 border border-white/10 px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-cyan-400"
              />
            </MetricCard>

            <MetricCard
              Icon={Heart}
              label="Heart rate"
              value={state.today.heart_rate ?? "—"}
              unit="bpm"
              accent="#f87171"
              testId="metric-hr"
            >
              <input
                data-testid="metric-hr-input"
                type="number"
                min="30"
                max="220"
                placeholder="bpm"
                value={state.today.heart_rate || ""}
                onChange={(e) => patch("heart_rate", Number(e.target.value) || null)}
                className="mt-2 w-full rounded-lg bg-white/5 border border-white/10 px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-cyan-400"
              />
            </MetricCard>

            <MetricCard
              Icon={Activity}
              label="Blood pressure"
              value={
                state.today.blood_pressure?.systolic
                  ? `${state.today.blood_pressure.systolic}/${state.today.blood_pressure.diastolic || "—"}`
                  : "—"
              }
              unit="mmHg"
              accent="#fb7185"
              testId="metric-bp"
            >
              <div className="mt-2 flex gap-2">
                <input
                  data-testid="metric-bp-sys"
                  type="number"
                  placeholder="Systolic"
                  value={state.today.blood_pressure?.systolic || ""}
                  onChange={(e) =>
                    setBP(e.target.value, state.today.blood_pressure?.diastolic)
                  }
                  className="w-full rounded-lg bg-white/5 border border-white/10 px-2 py-1.5 text-xs text-white focus:outline-none focus:border-cyan-400"
                />
                <input
                  data-testid="metric-bp-dia"
                  type="number"
                  placeholder="Diastolic"
                  value={state.today.blood_pressure?.diastolic || ""}
                  onChange={(e) =>
                    setBP(state.today.blood_pressure?.systolic, e.target.value)
                  }
                  className="w-full rounded-lg bg-white/5 border border-white/10 px-2 py-1.5 text-xs text-white focus:outline-none focus:border-cyan-400"
                />
              </div>
            </MetricCard>

            <MetricCard
              Icon={Flame}
              label="Blood sugar"
              value={state.today.blood_sugar ?? "—"}
              unit="mg/dL"
              accent="#fbbf24"
              testId="metric-sugar"
            >
              <input
                data-testid="metric-sugar-input"
                type="number"
                placeholder="mg/dL"
                value={state.today.blood_sugar || ""}
                onChange={(e) => patch("blood_sugar", Number(e.target.value) || null)}
                className="mt-2 w-full rounded-lg bg-white/5 border border-white/10 px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-cyan-400"
              />
            </MetricCard>

            <MetricCard
              Icon={Wind}
              label="SpO₂"
              value={state.today.spo2 ?? "—"}
              unit="%"
              accent="#34d399"
              testId="metric-spo2"
            >
              <input
                data-testid="metric-spo2-input"
                type="number"
                min="60"
                max="100"
                placeholder="%"
                value={state.today.spo2 || ""}
                onChange={(e) => patch("spo2", Number(e.target.value) || null)}
                className="mt-2 w-full rounded-lg bg-white/5 border border-white/10 px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-cyan-400"
              />
            </MetricCard>

            <MetricCard
              Icon={Thermometer}
              label="Temperature"
              value={state.today.temperature ?? "—"}
              unit="°F"
              accent="#fda4af"
              testId="metric-temp"
            >
              <input
                data-testid="metric-temp-input"
                type="number"
                step="0.1"
                placeholder="°F"
                value={state.today.temperature || ""}
                onChange={(e) => patch("temperature", Number(e.target.value) || null)}
                className="mt-2 w-full rounded-lg bg-white/5 border border-white/10 px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-cyan-400"
              />
            </MetricCard>
          </div>
        </motion.div>

        {/* Weight / BMI + Mood row */}
        <motion.div variants={item} className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div
            data-testid="weight-bmi-card"
            className="rounded-3xl border border-white/10 bg-white/[0.03] p-6"
          >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-semibold">
              <Scale className="w-3.5 h-3.5" />
              Body composition
            </div>
            <div className="mt-4 flex flex-wrap items-end gap-8">
              <div>
                <div className="text-[11px] text-zinc-400 uppercase tracking-widest">Height</div>
                <div className="mt-1 flex items-baseline gap-2">
                  <input
                    data-testid="profile-height"
                    type="number"
                    placeholder="170"
                    value={state.profile.height || ""}
                    onChange={(e) => {
                      const s = updateProfile({ height: Number(e.target.value) || null });
                      setState({ ...s });
                    }}
                    className="w-24 rounded-lg bg-white/5 border border-white/10 px-2.5 py-1.5 text-xl font-display text-white focus:outline-none focus:border-cyan-400"
                  />
                  <span className="text-xs text-zinc-500">cm</span>
                </div>
              </div>
              <div>
                <div className="text-[11px] text-zinc-400 uppercase tracking-widest">Weight</div>
                <div className="mt-1 flex items-baseline gap-2">
                  <input
                    data-testid="profile-weight"
                    type="number"
                    placeholder="70"
                    value={state.profile.weight || ""}
                    onChange={(e) => {
                      const s = updateProfile({ weight: Number(e.target.value) || null });
                      setState({ ...s });
                    }}
                    className="w-24 rounded-lg bg-white/5 border border-white/10 px-2.5 py-1.5 text-xl font-display text-white focus:outline-none focus:border-cyan-400"
                  />
                  <span className="text-xs text-zinc-500">kg</span>
                </div>
              </div>
              <div className="ml-auto text-right">
                <div className="text-[11px] text-zinc-400 uppercase tracking-widest">BMI</div>
                <div className="mt-1 font-display text-4xl font-semibold tabular-nums text-cyan-300">
                  {bmi ?? "—"}
                </div>
                {bmiCat && (
                  <div className="text-xs text-zinc-400 mt-0.5">{bmiCat}</div>
                )}
              </div>
            </div>
            <p className="mt-4 text-[11px] text-zinc-500 leading-relaxed">
              BMI is one signal, not a diagnosis. Body composition matters too.
            </p>
          </div>

          <div
            data-testid="mood-card"
            className="rounded-3xl border border-white/10 bg-white/[0.03] p-6"
          >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-semibold">
              <Sparkles className="w-3.5 h-3.5" />
              Today&apos;s mood
            </div>
            <div className="mt-4 grid grid-cols-5 gap-2">
              {MOODS.map((m) => {
                const active = state.today.mood === m.key;
                return (
                  <button
                    key={m.key}
                    data-testid={`mood-${m.key}`}
                    onClick={() => patch("mood", m.key)}
                    className={`aspect-square rounded-2xl border transition-all flex flex-col items-center justify-center gap-1 ${
                      active
                        ? "border-cyan-400 bg-cyan-400/10 scale-105"
                        : "border-white/10 bg-white/5 hover:bg-white/10"
                    }`}
                  >
                    <span className="text-2xl">{m.emoji}</span>
                    <span className="text-[10px] uppercase tracking-widest text-zinc-400">
                      {m.label}
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="mt-4 text-[11px] text-zinc-500 leading-relaxed">
              A one-tap emotional log helps you spot patterns week-over-week.
            </p>
          </div>
        </motion.div>

        {/* Trends + Score history */}
        <motion.div variants={item} className="mt-8">
          <TrendsPanel state={state} />
        </motion.div>
        <motion.div variants={item} className="mt-4">
          <ScoreHistory />
        </motion.div>

        {/* Missions + Calendar row */}
        <motion.div variants={item} className="mt-8 grid grid-cols-1 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] gap-4">
          {/* Missions */}
          <div
            data-testid="missions-card"
            className="rounded-3xl border border-white/10 bg-white/[0.03] p-6"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-semibold">
                <Trophy className="w-3.5 h-3.5" />
                Daily missions
              </div>
              <div className="text-[11px] text-zinc-400">
                {todayMissions.length}/{MISSIONS.length} · +
                {MISSIONS.filter((m) => todayMissions.includes(m.id)).reduce((s, m) => s + m.xp, 0)} XP
              </div>
            </div>
            <div className="mt-4 flex flex-col divide-y divide-white/5">
              {MISSIONS.map((m) => {
                const done = todayMissions.includes(m.id);
                return (
                  <button
                    key={m.id}
                    data-testid={`mission-${m.id}`}
                    onClick={() => onMission(m.id)}
                    className="flex items-center gap-3 py-3 text-left"
                  >
                    {done ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    ) : (
                      <Circle className="w-5 h-5 text-zinc-600 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <div
                        className={`text-sm ${
                          done ? "text-zinc-500 line-through" : "text-white"
                        }`}
                      >
                        {m.label}
                      </div>
                    </div>
                    <div
                      className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
                        done
                          ? "bg-emerald-400/10 text-emerald-300"
                          : "bg-white/5 text-zinc-400"
                      }`}
                    >
                      +{m.xp} XP
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Mini calendar */}
          <div
            data-testid="calendar-card"
            className="rounded-3xl border border-white/10 bg-white/[0.03] p-6"
          >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-semibold">
              <Calendar className="w-3.5 h-3.5" />
              This week
            </div>
            <MiniWeek missionsDone={state.missions_done} />
            <div className="mt-4 flex items-center justify-between rounded-2xl bg-black/40 border border-white/5 p-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                  Next reminder
                </div>
                <div className="text-sm text-white mt-0.5">Evening walk · 7:00 PM</div>
              </div>
              <Bell className="w-4 h-4 text-cyan-400" />
            </div>
            <div className="mt-3 flex items-center justify-between rounded-2xl bg-black/40 border border-white/5 p-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                  Suggested check-up
                </div>
                <div className="text-sm text-white mt-0.5">Annual blood panel</div>
              </div>
              <ChevronRight className="w-4 h-4 text-zinc-400" />
            </div>
          </div>
        </motion.div>

        {/* Quick actions grid */}
        <motion.div variants={item} className="mt-8">
          <SectionHeader chapter="Chapter 03" title="Your health rooms" subtitle="Everything you need in one place." />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <QuickAction
              testId="qa-assistant"
              onClick={() => (window.location.href = "/assistant")}
              Icon={Sparkles}
              title="AI Assistant"
              subtitle="Ask health questions"
              gradient="from-cyan-500/20 to-blue-500/10"
            />
            <QuickAction
              testId="qa-mental"
              onClick={openMental}
              Icon={Heart}
              title="Mental Wellness"
              subtitle="11-Q mood scan"
              gradient="from-rose-500/20 to-fuchsia-500/10"
            />
            <QuickAction
              testId="qa-diet"
              onClick={() => (window.location.href = "/nutrition")}
              Icon={Utensils}
              title="Nutrition Engine"
              subtitle="Macros · Region · Photo"
              gradient="from-emerald-500/20 to-teal-500/10"
            />
            <QuickAction
              testId="qa-simulator"
              onClick={() => (window.location.href = "/simulator")}
              Icon={Sparkles}
              title="Future Simulator"
              subtitle="See tomorrow, today"
              gradient="from-indigo-500/20 to-violet-500/10"
            />
            <QuickAction
              testId="qa-library"
              onClick={() => (window.location.href = "/library")}
              Icon={Bell}
              title="Knowledge Library"
              subtitle="Bite-sized articles"
              gradient="from-amber-500/20 to-orange-500/10"
            />
            <QuickAction
              testId="qa-medid"
              onClick={() => (window.location.href = "/medical-id")}
              Icon={Heart}
              title="Medical ID"
              subtitle="Wallet-style card"
              gradient="from-red-500/20 to-rose-500/10"
            />
            <QuickAction
              testId="qa-hospitals"
              onClick={() => (window.location.href = "/hospitals")}
              Icon={MapPin}
              title="Nearby Care"
              subtitle="Hospitals & clinics"
              gradient="from-teal-500/20 to-cyan-500/10"
            />
            <QuickAction
              testId="qa-wearables"
              onClick={() => (window.location.href = "/wearables")}
              Icon={Watch}
              title="Wearables"
              subtitle="Bluetooth · Live HR"
              gradient="from-blue-500/20 to-cyan-500/10"
            />
            <QuickAction
              testId="qa-sleep"
              onClick={() => (window.location.href = "/sleep")}
              Icon={BedDouble}
              title="Sleep"
              subtitle="Recovery score"
              gradient="from-violet-500/20 to-fuchsia-500/10"
            />
            <QuickAction
              testId="qa-family"
              onClick={() => (window.location.href = "/family")}
              Icon={Users}
              title="Family"
              subtitle="Shared profiles"
              gradient="from-emerald-500/20 to-teal-500/10"
            />
            <QuickAction
              testId="qa-community"
              onClick={() => (window.location.href = "/community")}
              Icon={MessageCircle}
              title="Community"
              subtitle="Cheer & share"
              gradient="from-amber-500/20 to-orange-500/10"
            />
            <QuickAction
              testId="qa-neurorehab"
              onClick={() => (window.location.href = "/neurorehab")}
              Icon={Sparkles}
              title="NeuroRehab AI"
              subtitle="Cognitive training"
              gradient="from-violet-500/20 to-fuchsia-500/10"
            />
            <QuickAction
              testId="qa-neurospeak"
              onClick={() => (window.location.href = "/neurospeak")}
              Icon={Sparkles}
              title="NeuroSpeak AI"
              subtitle="Communication training"
              gradient="from-emerald-500/20 to-teal-500/10"
            />
            <QuickAction
              testId="qa-neuromotor"
              onClick={() => (window.location.href = "/neuromotor")}
              Icon={Sparkles}
              title="NeuroMotor AI"
              subtitle="Motor-skill practice"
              gradient="from-orange-500/20 to-amber-500/10"
            />
            <QuickAction
              testId="qa-profile"
              onClick={() => (window.location.href = "/profile")}
              Icon={Users}
              title="My Profile"
              subtitle="Health & recovery"
              gradient="from-cyan-500/20 to-blue-500/10"
            />
            <QuickAction
              testId="qa-emergency"
              onClick={() => (window.location.href = "tel:108")}
              Icon={Phone}
              title="Emergency 108"
              subtitle="One-tap dial"
              gradient="from-red-500/20 to-orange-500/10"
            />
          </div>
        </motion.div>
      </motion.section>
    </div>
  );
}

// ---------------------------------------------------------------------------
const SectionHeader = ({ chapter, title, subtitle }) => (
  <div className="mb-4">
    <div className="text-[10px] font-semibold uppercase tracking-[0.35em] text-cyan-300/80">
      {chapter}
    </div>
    <h2 className="mt-1.5 font-display text-2xl sm:text-3xl font-semibold text-white leading-tight">
      {title}
    </h2>
    {subtitle && (
      <p className="mt-1 text-sm text-zinc-400 max-w-md">{subtitle}</p>
    )}
  </div>
);

const MetricCard = ({ Icon, label, value, unit, accent, testId, children }) => (
  <motion.div
    data-testid={testId}
    whileHover={{ y: -3 }}
    transition={{ duration: 0.2 }}
    className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-4"
  >
    <div
      aria-hidden
      className="absolute -top-8 -right-8 w-24 h-24 rounded-full blur-2xl opacity-40 group-hover:opacity-70 transition-opacity"
      style={{ background: accent }}
    />
    <div className="relative flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
      <Icon className="w-3.5 h-3.5" style={{ color: accent }} />
      {label}
    </div>
    <div className="relative mt-2 flex items-baseline gap-1.5">
      <div className="font-display text-3xl font-semibold text-white tabular-nums">
        {value}
      </div>
      {unit && <div className="text-xs text-zinc-500">{unit}</div>}
    </div>
    <div className="relative">{children}</div>
  </motion.div>
);

const StepChip = ({ onClick, children }) => (
  <button
    onClick={onClick}
    className="px-2.5 py-1 rounded-full text-[11px] bg-white/5 border border-white/10 text-zinc-200 hover:bg-white/10"
  >
    {children}
  </button>
);

const QuickAction = ({ Icon, title, subtitle, onClick, gradient, testId }) => (
  <motion.button
    data-testid={testId}
    whileHover={{ y: -3 }}
    onClick={onClick}
    className={`relative text-left overflow-hidden rounded-3xl border border-white/10 p-5 bg-gradient-to-br ${gradient} hover:bg-white/[0.06]`}
  >
    <Icon className="w-6 h-6 text-white mb-4" />
    <div className="font-display text-lg font-semibold text-white">{title}</div>
    <div className="text-xs text-zinc-300 mt-1">{subtitle}</div>
    <ChevronRight className="absolute bottom-4 right-4 w-4 h-4 text-white/60" />
  </motion.button>
);

const MiniWeek = ({ missionsDone }) => {
  const today = new Date();
  const days = Array.from({ length: 7 }).map((_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() - (6 - i));
    return d;
  });
  return (
    <div className="mt-4 grid grid-cols-7 gap-1.5">
      {days.map((d, i) => {
        const key = d.toISOString().slice(0, 10);
        const count = (missionsDone[key] || []).length;
        const active = count >= 3;
        const partial = count > 0 && count < 3;
        const isToday = i === 6;
        return (
          <div key={key} className="flex flex-col items-center gap-1">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">
              {d.toLocaleDateString("en-US", { weekday: "short" }).slice(0, 1)}
            </div>
            <div
              className={`w-full aspect-square rounded-lg border flex items-center justify-center text-[11px] font-medium tabular-nums ${
                active
                  ? "border-emerald-400/60 bg-emerald-400/20 text-emerald-200"
                  : partial
                  ? "border-cyan-400/40 bg-cyan-400/10 text-cyan-200"
                  : "border-white/10 bg-white/5 text-zinc-500"
              } ${isToday ? "ring-1 ring-white/40" : ""}`}
            >
              {d.getDate()}
            </div>
          </div>
        );
      })}
    </div>
  );
};
